import { observer } from "mobx-react-lite";

import { useAuthState } from "@synthesis/shared-auth";
import { IconLogout } from "@synthesis/shared-icons";
import { useGetUserGroups } from "@synthesis/shared-services/auth/group/hooks";
import { Breadcrumbs, Tag } from "@synthesis/shared-ui";

import { useBreadcrumbs, usePopoverInfo } from "./hooks";

type RoleRight = "СУПЕР" | "АДМИНИСТРИРОВАНИЕ" | "МОДЕРИРОВАНИЕ" | "ПРОСМОТР" | "НЕТ";

const roleName: Record<RoleRight, string> = {
  СУПЕР: "Суперадмин",
  АДМИНИСТРИРОВАНИЕ: "Админ",
  МОДЕРИРОВАНИЕ: "Модератор",
  ПРОСМОТР: "Просмотр",
  НЕТ: "Нет роли",
};

const useQueryGroup = (userId: string) => {
  const { data } = useGetUserGroups({ path: { userId } }, { query: { enabled: !!userId } });
  return data?.[0]?.name ?? "";
};

export const Header = observer(() => {
  const { userShortName, handleLogout, userLastNameAndInitials } = usePopoverInfo();
  const breadcrumbsList = useBreadcrumbs();
  const authStore = useAuthState();
  const groupName = useQueryGroup(authStore.currentUser?.id ?? "");

  const currentUser = authStore.currentUser;
  const roles = currentUser?.roles;
  const foundRole = roles?.find((role: string) => role.startsWith("ПЛАТФОРМА 3Д"));
  const roleRight = foundRole?.split(" ")[2] as RoleRight | undefined;
  const roleDisplay = roleRight ? roleName[roleRight] || "Неизвестная роль" : null;

  return (
    <header className="bg-neutrals-99 sticky top-0 z-50 flex h-[70px] items-center justify-between pl-5 pr-8">
      {breadcrumbsList.length > 0 && <Breadcrumbs items={breadcrumbsList} />}
      <div className="flex w-full items-center justify-end gap-5">
        <div className="relative inline-block">
          <figure className="bg-neutrals-90 text-labels-secondary flex size-10 shrink-0 items-center justify-center rounded-full">
            {userShortName}
          </figure>
          {roleDisplay && (
            <Tag
              className="absolute left-1/2 top-7 w-fit max-w-none -translate-x-1/2 whitespace-nowrap rounded-lg px-1 [&_span]:w-auto"
              color="red"
              size="sm"
            >
              {roleDisplay}
            </Tag>
          )}
        </div>

        <div className="flex flex-col items-center justify-center">
          <span className="text-type-minor text-sm font-normal leading-5">{userLastNameAndInitials}</span>
          <span className="text-type-light text-xs font-normal leading-4">{groupName}</span>
        </div>

        <div tabIndex={0} className="cursor-pointer" onClick={() => handleLogout()}>
          <IconLogout className="text-neutrals-60 size-6" />
        </div>
      </div>
    </header>
  );
});
